import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Play } from 'lucide-react';
import { Button } from '../ui/button';

// Extract YouTube video ID from various URL formats
const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
    /(?:youtube\.com\/embed\/)([^?\s]+)/,
    /(?:youtu\.be\/)([^?\s]+)/,
    /(?:youtube\.com\/v\/)([^?\s]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

/**
 * Single video card — equal sized, opens YouTube link in a new tab on click.
 */
const VizyonVideoCard = ({ video }) => {
  const videoId = getYouTubeId(video.youtube_url);
  const thumbnailUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : video.image_url;

  // Always link to the internal news detail page; open in a new tab
  const internalHref = `/haber/${video.slug || video.id}`;

  return (
    <a
      href={internalHref}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`vizyon-video-card-${video.id}`}
      className="group flex-shrink-0 w-[280px] md:w-[300px] snap-start"
    >
      <div className="relative rounded-lg overflow-hidden bg-slate-800 ring-1 ring-white/5 transition-all duration-300 group-hover:ring-blue-500/60 group-hover:shadow-2xl group-hover:shadow-blue-500/10">
        {/* Thumbnail — fixed aspect ratio for equal sizing */}
        <div className="aspect-video relative overflow-hidden">
          <img
            src={thumbnailUrl}
            alt={video.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              if (video.image_url && e.target.src !== video.image_url) {
                e.target.src = video.image_url;
              }
            }}
          />
          {/* Play overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center opacity-90 group-hover:opacity-100 transition-opacity">
            <div className="bg-blue-600/90 backdrop-blur-sm rounded-full p-3 shadow-xl transform transition-transform duration-300 group-hover:scale-110">
              <Play className="w-5 h-5 text-white fill-white" />
            </div>
          </div>
          {/* Branding chip */}
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wider rounded">
              Vizyon
            </span>
          </div>
        </div>
        {/* Title block — equal height for card uniformity */}
        <div className="p-4 bg-slate-900">
          <h3 className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-blue-400 transition-colors min-h-[2.5rem]">
            {video.title}
          </h3>
        </div>
      </div>
    </a>
  );
};

/**
 * Horizontal carousel for Vizyon Sektör videos.
 * - Equal sized cards in a single horizontal row
 * - Sleek forward-navigation arrow on the far left
 * - Smooth scroll behavior with snap points
 */
const VizyonSektorCarousel = ({ videos = [] }) => {
  const scrollRef = useRef(null);
  const [canScroll, setCanScroll] = useState(false);

  const checkScrollable = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScroll(el.scrollWidth > el.clientWidth + 8);
  }, []);

  useEffect(() => {
    checkScrollable();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkScrollable);
    ro.observe(el);
    window.addEventListener('resize', checkScrollable);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', checkScrollable);
    };
  }, [videos, checkScrollable]);

  const scrollForward = () => {
    const el = scrollRef.current;
    if (!el) return;
    // Scroll by ~one card width to advance forward
    const step = Math.max(el.clientWidth * 0.7, 320);
    // If at end, loop back to start for smooth UX
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    el.scrollTo({
      left: atEnd ? 0 : el.scrollLeft + step,
      behavior: 'smooth',
    });
  };

  if (!videos || videos.length === 0) return null;

  return (
    <section
      className="bg-[#0A0F1A] py-12 relative overflow-hidden"
      data-testid="vizyon-section"
    >
      {/* Subtle radial accent */}
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Header — branding preserved */}
        <div className="flex items-end justify-between mb-7">
          <div className="flex items-center gap-3">
            <div className="w-1 h-10 bg-blue-500 rounded-full" />
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                VİZYON SEKTÖR
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                İş dünyasının öncü liderleriyle röportajlar
              </p>
            </div>
          </div>
          <Link to="/vizyon-sektor" data-testid="vizyon-see-all">
            <Button
              variant="outline"
              className="border-blue-500/40 bg-transparent text-blue-300 hover:bg-blue-500/10 hover:text-blue-200 hidden md:flex"
            >
              Tümünü Gör <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Carousel container */}
        <div className="relative">
          {/* Forward navigation arrow — far left */}
          {canScroll && (
            <button
              type="button"
              onClick={scrollForward}
              data-testid="vizyon-scroll-forward"
              aria-label="İleri kaydır"
              className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-11 h-11 rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/40 ring-2 ring-[#0A0F1A] transition-all duration-200 hover:scale-110 active:scale-95"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          {/* Edge fade for premium feel */}
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[#0A0F1A] to-transparent z-10" />

          {/* Scrollable row */}
          <div
            ref={scrollRef}
            data-testid="vizyon-scroll-container"
            className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 -mx-1 px-1 vizyon-scrollbar"
          >
            {videos.map((video) => (
              <VizyonVideoCard key={`viz-${video.id}`} video={video} />
            ))}
          </div>
        </div>

        {/* Mobile: see all link */}
        <div className="md:hidden mt-5 text-center">
          <Link
            to="/vizyon-sektor"
            className="inline-flex items-center text-blue-400 text-sm font-semibold hover:text-blue-300"
          >
            Tümünü Gör <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default VizyonSektorCarousel;
